import React, { useState, useEffect } from "react";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import { useBootstrap, useMui, useSetting } from "../../hooks/Hooks";
import CryptoJS from "crypto-js";
import "./ViewInvoiceStatment.css";
import dayjs from "dayjs";
import { formatToIndianCurrency } from "../../hooks/Function";
import Axios from "../../setting/axios";
import SmallInvoiceTable from "../../components/SmallDataTable/SmallInvoiceTable";

const ViewInvoiceStatement = ({ dispatch }) => {
  const { Card, Col, Row } = useBootstrap();
  const {
    ArrowBackIosIcon,
    Divider,
    PrintIcon,
    Button,
    FileDownloadOutlinedIcon,
  } = useMui();
  const { globalData } = useSetting();

  // State
  const [statementDetails, setStatementDetails] = useState(null);
  const [statementData, setStatementData] = useState(null);
  const [logs, setLogs] = useState([]);

  // Transaction Amount Calculation Function
  const calculateTransactionAmount = (invoice_amt, roi, tenure) => {
    if (!invoice_amt || !roi || !tenure) {
      console.warn("⚠️ Invalid inputs for transaction amount calculation:", {
        invoice_amt,
        roi,
        tenure,
      });
      return 0;
    }

    const amount = parseFloat(invoice_amt);
    const rate = parseFloat(roi);
    const days = parseInt(tenure);

    if (isNaN(amount) || isNaN(rate) || isNaN(days)) {
      console.warn("⚠️ NaN values in calculation:", { amount, rate, days });
      return 0;
    }

    const interest = (amount * rate * days) / (365 * 100);
    const result = parseFloat(interest.toFixed(2));

    return result;
  };

  // Format calculation display
  const formatCalculation = (log) => {
    if (!log || !statementData) return "N/A";

    const billingType = statementData.billing_type;

    if (billingType === "Transaction Amount") {
      const disbursement_amount = parseFloat(log.disbursement_amount) || 0;
      const roi = parseFloat(statementData.user_roi || log.user_roi) || 0;
      const tenure = parseInt(log.tenure) || 0;

      if (disbursement_amount && roi && tenure) {
        const calculatedAmount = calculateTransactionAmount(
          disbursement_amount,
          roi,
          tenure
        );
        return (
          <div className="calculation-display">
            <div className="formula-text" style={{ color: "#000" }}>
              ({formatToIndianCurrency(disbursement_amount)} × {roi}% × {tenure}{" "}
              days) ÷ 365
            </div>
          </div>
        );
      }
    }

    return (
      <span style={{ color: "#999", fontStyle: "italic" }}>No calculation</span>
    );
  };

  // Decrypt statement data
  useEffect(() => {
    const decryptData = () => {
      try {
        const key = globalData.CompanyName + "@" + globalData.Version;
        const bytes = CryptoJS.AES.decrypt(
          localStorage.getItem("updateglobal_userdata"),
          key
        );
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const parsed = JSON.parse(decrypted);
        setStatementDetails(parsed);
      } catch (error) {
        console.error("❌ Error decrypting statement data:", error);
        setStatementDetails(null);
      }
    };

    decryptData();
  }, [globalData.CompanyName, globalData.Version]);

  // Fetch statement data and logs
  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = `/invoice-statement/statement/${statementDetails.inv_statement_id}`;
        const res = await Axios.get(url);

        setStatementData(res.data.statement);
        setLogs(res.data.logs || []);
      } catch (err) {
        console.error("❌ Error fetching statement data:", {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          url: err.config?.url,
        });

        setStatementData(null);
        setLogs([]);
      }
    };

    if (statementDetails?.inv_statement_id) {
      fetchData();
    }
  }, [statementDetails]);
  console.log("statementData: ", statementData);

  // Helper function to get month from date
  const getMonthFromDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00") {
      return "N/A";
    }
    return dayjs(dateString).format("MMMM YYYY");
  };

  // Get month from start_date (assuming start and end dates are in same month)
  const month = getMonthFromDate(statementData?.start_date);

  // Dates - Fixed with optional chaining and null checks
  const start_date =
    statementData?.start_date && statementData.start_date !== "0000-00-00"
      ? dayjs(statementData.start_date).format("D MMM YYYY")
      : "N/A";

  const end_date =
    statementData?.end_date && statementData.end_date !== "0000-00-00"
      ? dayjs(statementData.end_date).format("D MMM YYYY")
      : "N/A";

  const created_at =
    statementData?.created_at &&
    statementData.created_at !== "0000-00-00 00:00:00"
      ? dayjs(statementData.created_at).format("D MMM YYYY - hh:mm A")
      : "N/A";

  const instituteNames = Array.isArray(statementData?.institute_name)
    ? statementData.institute_name.join(", ") || "N/A"
    : typeof statementData?.institute_name === "string"
    ? statementData.institute_name
    : "N/A";

  // Statement Details Array
  const statementBasicDetailsArr = [
    { label: "Client Name", desc: statementData?.company_name || "N/A" },
    ...(statementData?.billing_type === "Lumpsum Monthly"
      ? [{ label: "Month", desc: month }]
      : [
          { label: "Start Date", desc: start_date },
          { label: "End Date", desc: end_date },
        ]),
    { label: "Billing Type", desc: statementData?.billing_type || "N/A" },
    ...(statementData?.billing_type === "Transaction Amount"
      ? [
          {
            label: "Charges at the Rate",
            desc: `${statementData?.user_roi || "N/A"}%`,
          },
        ]
      : []),
  ];

  // Updated columns for SmallInvoiceTable - Remove billing amount column for Lumpsum Monthly as it will be handled separately
  const invoiceColumns = [
    {
      Header: "Lender Name",
      accessor: "institute_name",
    },
    {
      Header: "Invoice Number",
      accessor: "invoice_number",
    },
    {
      Header: "Discounting Date",
      accessor: "discounting_date",
      Cell: ({ value }) =>
        value && value !== "0000-00-00"
          ? dayjs(value).format("D MMM YYYY")
          : "N/A",
    },
    {
      Header: "Invoice Amount",
      accessor: "invoice_amount",
      Cell: ({ value }) => formatToIndianCurrency(value || 0),
    },
    {
      Header: "Disbusment Amount",
      accessor: "disbursement_amount",
      Cell: ({ value }) => formatToIndianCurrency(value || 0),
    },
    {
      Header: "Number of days",
      accessor: "tenure",
    },
    // New Calculations column - only show for Transaction Amount billing type
    // ...(statementData?.billing_type === "Transaction Amount"
    //   ? [
    //       {
    //         Header: "Calculation",
    //         accessor: "calculation",
    //         Cell: ({ row }) => {
    //           return formatCalculation(row.original);
    //         },
    //         minWidth: 200,
    //       },
    //     ]
    //   : []),
    // Billing Amount column - only show if NOT Lumpsum Monthly (handled separately now)
    ...(statementData?.billing_type !== "Lumpsum Monthly"
      ? [
          {
            Header: "Billing Amount (₹)",
            accessor: "billing_amount",
            Cell: ({ value, row }) => {
              const billingAmount = parseFloat(value) || 0;

              // For Transaction Amount billing type, show calculated vs actual
              if (statementData?.billing_type === "Transaction Amount") {
                const invoice_amt =
                  parseFloat(row.original.invoice_amount) || 0;
                const roi =
                  parseFloat(statementData.user_roi || row.original.user_roi) ||
                  0;
                const tenure = parseInt(row.original.tenure) || 0;

                if (invoice_amt && roi && tenure) {
                  const calculatedAmount = calculateTransactionAmount(
                    invoice_amt,
                    roi,
                    tenure
                  );
                  const isMatching =
                    Math.abs(billingAmount - calculatedAmount) < 0.01;

                  return (
                    <div>
                      <div
                        style={{
                          color: isMatching ? "#000" : "#000",
                        }}
                      >
                        {formatToIndianCurrency(billingAmount)}
                      </div>
                      {/* {!isMatching && (
                        <div style={{ fontSize: "10px", color: "#ff9800" }}>
                          Expected: {formatToIndianCurrency(calculatedAmount)}
                        </div>
                      )} */}
                    </div>
                  );
                }
              }

              return formatToIndianCurrency(billingAmount);
            },
          },
        ]
      : []),
  ];

  // Print
  const handlePrint = () => {
    const printContent = document.getElementById("printSection").innerHTML;
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // Verify calculations for Transaction Amount billing type
  const verifyCalculations = () => {
    if (
      statementData?.billing_type === "Transaction Amount" &&
      logs.length > 0
    ) {
      logs.forEach((log, index) => {
        const invoice_amt = parseFloat(log.invoice_amount) || 0;
        const roi = parseFloat(statementData.user_roi || log.user_roi) || 0;
        const tenure = parseInt(log.tenure) || 0;
        const actualBilling = parseFloat(log.billing_amount) || 0;

        if (invoice_amt && roi && tenure) {
          const calculatedAmount = calculateTransactionAmount(
            invoice_amt,
            roi,
            tenure
          );
          const difference = Math.abs(actualBilling - calculatedAmount);
          const isAccurate = difference < 0.01;
        }
      });

      // Calculate total
      const totalCalculated = logs.reduce((sum, log) => {
        const invoice_amt = parseFloat(log.invoice_amount) || 0;
        const roi = parseFloat(statementData.user_roi || log.user_roi) || 0;
        const tenure = parseInt(log.tenure) || 0;

        if (invoice_amt && roi && tenure) {
          return sum + calculateTransactionAmount(invoice_amt, roi, tenure);
        }
        return sum;
      }, 0);

      const totalActual = logs.reduce(
        (sum, log) => sum + (parseFloat(log.billing_amount) || 0),
        0
      );
    }
  };

  // Run verification when data loads
  useEffect(() => {
    if (statementData && logs.length > 0) {
      verifyCalculations();
    }
  }, [statementData, logs]);

  // Show loading or error before data is ready
  if (!statementDetails || !statementData) {
    return <div>Loading or failed to load statement data</div>;
  }

  return (
    <>
      <Breadcrumb
        PageName="View Invoice Statement"
        BackButton={[
          true,
          "Back",
          <ArrowBackIosIcon />,
          () =>
            dispatch({
              event:
                localStorage.getItem("previousScreen") ===
                localStorage.getItem("currScreen")
                  ? "invoicestatements"
                  : localStorage.getItem("previousScreen"),
            }),
        ]}
      />
      <span id="printSection">
        {/* Statement Details */}
        <Card className="my-3 card-bg">
          <Card.Body>
            <Row>
              <Col md={6}>
                <Card.Title className="fs-6">Statement Details</Card.Title>
              </Col>
              <Col md={6} className="text-end">
                <Button
                  className="btn btn-sm primary-btn print-exclude"
                  variant="contained"
                  startIcon={<PrintIcon />}
                  sx={{
                    fontSize: "12px",
                    textTransform: "capitalize",
                    marginTop: "-18px",
                  }}
                  onClick={handlePrint}
                >
                  Print
                </Button>
              </Col>
            </Row>
            <Divider />
            <Row>
              <Col md={12} sm={12} className="custom-view-section">
                <Row>
                  {statementBasicDetailsArr.map((details, id) =>
                    details.desc && details.desc !== "null" ? (
                      <Col md={3} sm={3} key={id}>
                        <p className="custom-subhead custom-form-title">
                          <p className="custom-subhead mb-1">{details.label}</p>
                          {details.desc}
                        </p>
                      </Col>
                    ) : null
                  )}
                </Row>
              </Col>
            </Row>
            <Divider />
          </Card.Body>
        </Card>

        {/* Billing Details */}
        {statementData?.has_billing && (
          <Card className="my-3 card-bg">
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Card.Title className="fs-6">Billing Details</Card.Title>
                </Col>
                <Col md={6} className="text-end">
                  {statementDetails?.statement_file && (
                    <Button
                      className="btn secondary-btn print-exclude"
                      variant="contained"
                      startIcon={<FileDownloadOutlinedIcon />}
                      href={
                        globalData.API_URL +
                        "/" +
                        statementDetails.statement_file
                      }
                      sx={{
                        fontSize: "12px",
                        textTransform: "capitalize",
                        marginTop: "-18px",
                      }}
                      target="_blank"
                    >
                      Download Statement
                    </Button>
                  )}
                </Col>
              </Row>
              <Divider />
              <Divider />

              {/* Invoice Table - Updated to pass billing type and total billing amount */}
              {logs && logs.length > 0 && (
                <>
                  <SmallInvoiceTable
                    columns={invoiceColumns}
                    data={logs}
                    billingType={statementData?.billing_type}
                    totalBillingAmount={
                      statementData?.billing_type === "Lumpsum Monthly"
                        ? formatToIndianCurrency(
                            logs.reduce(
                              (sum, log) =>
                                sum + (parseFloat(log.billing_amount) || 0),
                              0
                            )
                          )
                        : null
                    }
                  />
                  <div className="text-end fw-bold mt-3">
                    Grand Total:
                    <span
                      style={{
                        color: "#fe7a24",
                      }}
                    >
                      {" "}
                      {formatToIndianCurrency(
                        logs.reduce(
                          (sum, log) =>
                            sum + (parseFloat(log.billing_amount) || 0),
                          0
                        )
                      )}
                    </span>
                    {/* Show calculated total for Transaction Amount billing type */}
                    {statementData?.billing_type === "Transaction Amount" &&
                      (() => {
                        const calculatedTotal = logs.reduce((sum, log) => {
                          const invoice_amt =
                            parseFloat(log.invoice_amount) || 0;
                          const roi =
                            parseFloat(
                              statementData.user_roi || log.user_roi
                            ) || 0;
                          const tenure = parseInt(log.tenure) || 0;

                          if (invoice_amt && roi && tenure) {
                            return (
                              sum +
                              calculateTransactionAmount(
                                invoice_amt,
                                roi,
                                tenure
                              )
                            );
                          }
                          return sum;
                        }, 0);

                        const actualTotal = logs.reduce(
                          (sum, log) =>
                            sum + (parseFloat(log.billing_amount) || 0),
                          0
                        );
                        const isMatching =
                          Math.abs(calculatedTotal - actualTotal) < 0.01;

                        // if (!isMatching) {
                        //   return (
                        //     <div
                        //       style={{
                        //         fontSize: "12px",
                        //         color: "#ff9800",
                        //         marginTop: "5px",
                        //       }}
                        //     >
                        //       Calculated Total:{" "}
                        //       {formatToIndianCurrency(calculatedTotal)}
                        //     </div>
                        //   );
                        // }
                        // return null;
                      })()}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </span>
    </>
  );
};

export default ViewInvoiceStatement;
